import {
    DataTable,
    DateField,
    EditButton,
    NumberField,
    ReferenceField,
    ReferenceManyField,
    Show,
    TextField,
} from '@/components/admin';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Briefcase,
    Calendar,
    CircleDollarSign,
    FileSearch,
    FileStack,
    Layers,
    User,
    Users,
    Wrench
} from 'lucide-react';
import { useGetList, useRecordContext } from 'ra-core';
import { useState } from 'react';
import { ActivityTimeline } from '../activities/ActivityTimeline';
import { StatusBadge } from '../documents/shared/StatusBadge';
import { CreateProjectDesignRequestButton } from './CreateProjectDesignRequestButton';
import { CreateProjectDirectPOButton } from './CreateProjectDirectPOButton';
import { CreateProjectQuotationButton } from './CreateProjectQuotationButton';
import { LinkVendorButton } from './LinkVendorButton';
import { ProjectCostEntries } from './cost_entries/ProjectCostEntries';

const inr = (v: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v);

const ProjectShowActions = () => (
    <div className="flex items-center gap-2">
        <CreateProjectQuotationButton />
        <CreateProjectDesignRequestButton />
        <EditButton />
    </div>
);

const ProjectOverview = () => {
    const record = useRecordContext();
    if (!record) return null;

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
            <Card className="md:col-span-2">
                <CardContent className="pt-6 space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">Customer</span>
                            <ReferenceField source="company_id" reference="companies" link="show">
                                <TextField source="name" className="font-bold block text-primary" />
                            </ReferenceField>
                        </div>
                        <div className="space-y-1 text-right">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">Project Type</span>
                            <div className="font-semibold text-slate-700">{record.project_type || "Standard"}</div>
                        </div>
                    </div>

                    <div className="border-t pt-4">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight block mb-2">Description / Objectives</span>
                        <div className="text-sm text-slate-600 leading-relaxed bg-slate-50 p-4 rounded border border-dashed">
                            {record.description || "No detailed description provided for this project."}
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 border-t pt-4">
                        <div className="space-y-1">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight flex items-center gap-1">
                                <Calendar className="w-3 h-3" /> Start Date
                            </span>
                            <DateField source="start_date" className="text-sm font-medium" />
                        </div>
                        <div className="space-y-1 text-center">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight flex items-center gap-1 justify-center">
                                <Calendar className="w-3 h-3" /> Estimated Completion
                            </span>
                            <DateField source="expected_completion" className="text-sm font-medium" />
                        </div>
                        <div className="space-y-1 text-right">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight flex items-center gap-1 justify-end">
                                <Calendar className="w-3 h-3 text-green-500" /> Actual Completion
                            </span>
                            <DateField source="actual_completion" className="text-sm font-medium" />
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card className="bg-slate-900 text-white">
                <CardContent className="pt-6 space-y-8">
                    <div className="space-y-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                            <CircleDollarSign className="w-3 h-3" /> Financial Progress
                        </span>
                        <div className="pt-2">
                            <div className="text-2xl font-black">
                                <NumberField source="actual_value" options={{ style: 'currency', currency: 'INR' }} />
                            </div>
                            <div className="text-[10px] text-slate-400 italic">Actual Value Invoiced</div>
                        </div>
                    </div>

                    <div className="space-y-1 py-4 border-t border-slate-800">
                        <div className="flex justify-between items-end">
                            <span className="text-[11px] text-slate-400">Budgeted Value</span>
                            <span className="font-bold">
                                <NumberField source="estimated_value" options={{ style: 'currency', currency: 'INR' }} />
                            </span>
                        </div>
                    </div>

                    <div className="space-y-1 border-t border-slate-800 pt-4">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-2">
                            <User className="w-3 h-3" /> Project Manager
                        </span>
                        <ReferenceField source="project_manager" reference="sales" link="show">
                            <div className="flex items-center gap-3 bg-slate-800 p-3 rounded-lg border border-slate-700">
                                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center font-bold text-xs uppercase text-white">
                                    <TextField source="first_name" className="max-w-[15px] overflow-hidden whitespace-nowrap" />
                                </div>
                                <span className="font-bold text-sm"><TextField source="first_name" /> <TextField source="last_name" /></span>
                            </div>
                        </ReferenceField>
                    </div>
                </CardContent>
            </Card>
            
            <div className="md:col-span-3 lg:col-span-3 mt-4 h-[600px]">
                <ActivityTimeline resource="projects" recordId={record.id} />
            </div>
        </div>
    );
};

const DocStatusCell = ({ colors }: { colors: Record<string, string> }) => {
    const record = useRecordContext();
    if (!record) return null;
    const cls = colors[record.status] || 'bg-slate-50 text-slate-500 border-slate-200';
    return (
        <Badge variant="outline" className={`text-[10px] font-bold uppercase ${cls}`}>
            {record.status}
        </Badge>
    );
};

const QuotStatusCell = () => <DocStatusCell colors={{
    draft: 'bg-slate-100 text-slate-700 border-slate-300',
    sent: 'bg-blue-50 text-blue-700 border-blue-200',
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
    revised: 'bg-amber-50 text-amber-700 border-amber-200',
}} />;

const POStatusCell = () => <DocStatusCell colors={{
    draft: 'bg-slate-100 text-slate-700 border-slate-300',
    sent: 'bg-blue-50 text-blue-700 border-blue-200',
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
    revised: 'bg-amber-50 text-amber-700 border-amber-200',
}} />;

const InrCell = () => {
    const record = useRecordContext();
    if (!record?.total_amount) return <span className="text-slate-400 text-xs">—</span>;
    return <span className="font-bold text-sm">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(record.total_amount)}</span>;
};

const ProjectDocuments = () => (
    <div className="space-y-8 pt-4">
        <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3 px-1 flex items-center gap-2">
                <FileStack className="w-4 h-4" /> Quotations &amp; PIs
            </h3>
            <ReferenceManyField reference="quotations" target="project_id">
                <DataTable rowClick="show">
                    <DataTable.Col source="reference" label="REF" className="font-mono font-bold text-blue-700" />
                    <DataTable.Col source="date" label="DATE" />
                    <DataTable.Col label="STATUS"><QuotStatusCell /></DataTable.Col>
                    <DataTable.Col label="TOTAL (₹)"><InrCell /></DataTable.Col>
                </DataTable>
            </ReferenceManyField>
        </div>

        <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3 px-1 flex items-center gap-2">
                <Layers className="w-4 h-4" /> Purchase Orders
                <span className="ml-1 text-[10px] font-normal text-slate-400 normal-case tracking-normal">(click row to open)</span>
            </h3>
            <ReferenceManyField reference="purchase_orders" target="project_id">
                <DataTable rowClick="show">
                    <DataTable.Col source="reference" label="PO NUMBER" className="font-mono font-bold text-slate-900" />
                    <DataTable.Col label="VENDOR">
                        <ReferenceField source="vendor_id" reference="vendors">
                            <TextField source="name" className="text-xs font-semibold text-blue-600" />
                        </ReferenceField>
                    </DataTable.Col>
                    <DataTable.Col source="date" label="DATE" />
                    <DataTable.Col label="STATUS"><POStatusCell /></DataTable.Col>
                    <DataTable.Col label="TOTAL (₹)"><InrCell /></DataTable.Col>
                </DataTable>
            </ReferenceManyField>
        </div>
    </div>
);

const ProjectVendors = () => (
    <div className="pt-4 space-y-4">
        <div className="flex justify-between items-center px-1">
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
                <Users className="w-4 h-4" /> Associated Project Vendors
            </h3>
            <LinkVendorButton />
        </div>
        <ReferenceManyField reference="project_vendors" target="project_id">
            <DataTable>
                <DataTable.Col source="vendor_id" label="VENDOR">
                    <ReferenceField source="vendor_id" reference="vendors">
                        <TextField source="name" className="font-bold" />
                    </ReferenceField>
                </DataTable.Col>
                <DataTable.Col source="role" label="PROJECT ROLE" />
                <DataTable.Col source="linked_at" label="ASSOCIATED ON" />
                <DataTable.Col label="ACTIONS">
                    <CreateProjectDirectPOButton />
                </DataTable.Col>
            </DataTable>
        </ReferenceManyField>
    </div>
);

const ProjectDesign = () => (
    <div className="space-y-8 pt-4">
        <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 px-1 flex items-center gap-2">
                <Wrench className="w-4 h-4" /> Design Requests
            </h3>
            <ReferenceManyField reference="design_requests" target="project_id">
                <DataTable>
                    <DataTable.Col source="reference" label="REF" className="font-mono font-bold" />
                    <DataTable.Col source="title" label="TITLE" className="max-w-[200px] truncate" />
                    <DataTable.Col source="status" label="STATUS" />
                    <DataTable.Col source="priority" label="PRIORITY" />
                </DataTable>
            </ReferenceManyField>
        </div>

        <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 px-1 flex items-center gap-2">
                <Layers className="w-4 h-4" /> Bill of Materials (BoM)
            </h3>
            <ReferenceManyField reference="boms" target="project_id">
                <DataTable>
                    <DataTable.Col source="reference" label="REF" className="font-mono font-bold" />
                    <DataTable.Col source="version" label="VER" />
                    <DataTable.Col source="status" label="STATUS" />
                    <DataTable.Col source="total_estimated_cost" label="TOTAL COST" />
                </DataTable>
            </ReferenceManyField>
        </div>

        <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 px-1 flex items-center gap-2">
                <FileSearch className="w-4 h-4" /> Related RFQs
            </h3>
            <ReferenceManyField reference="rfqs" target="project_id">
                <DataTable>
                    <DataTable.Col source="reference" label="REF" className="font-mono font-bold" />
                    <DataTable.Col source="status" label="STATUS" />
                    <DataTable.Col source="due_date" label="DUE DATE" />
                </DataTable>
            </ReferenceManyField>
        </div>
    </div>
);

const ProjectFinancials = () => {
    const record = useRecordContext();
    const [totalCostEntries, setTotalCostEntries] = useState(0);

    const { data: invoices } = useGetList('customer_invoices', {
        pagination: { page: 1, perPage: 200 },
        filter: { project_id: record?.id },
    });
    const { data: pos } = useGetList('purchase_orders', {
        pagination: { page: 1, perPage: 200 },
        filter: { project_id: record?.id },
    });
    if (!record) return null;

    const totalInvoiced = invoices?.reduce((s: number, i: any) => s + (i.total_amount || 0), 0) ?? 0;
    const totalPaid = invoices?.reduce((s: number, i: any) => s + (i.paid_amount || 0), 0) ?? 0;
    const totalPOSpend = pos?.reduce((s: number, p: any) => s + (p.total_amount || 0), 0) ?? 0;
    const outstanding = totalInvoiced - totalPaid;
    const margin = totalInvoiced > 0 ? ((totalInvoiced - totalPOSpend) / totalInvoiced) * 100 : 0;
    const estimated = record.estimated_value || 0;
    // Variances are only meaningful once there's a budget AND actuals to compare.
    // Otherwise (e.g. budget set but nothing invoiced yet) the raw formula reads
    // a misleading -100%. See bug tracker Project #12.
    const hasRevenue = totalInvoiced > 0;
    const hasCosts = totalCostEntries > 0;
    const variance = estimated > 0 && hasRevenue ? ((totalInvoiced - estimated) / estimated) * 100 : null;
    const costVariance = estimated > 0 && hasCosts ? ((totalCostEntries - estimated) / estimated) * 100 : null;

    return (
        <div className="space-y-8 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card>
                    <CardContent className="pt-6 text-center">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Revenue (Invoiced)</div>
                        <div className="text-2xl font-black text-emerald-600">{inr(totalInvoiced)}</div>
                        <div className="text-xs text-slate-500 mt-1">{invoices?.length ?? 0} invoices</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6 text-center">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Collected</div>
                        <div className="text-2xl font-black text-blue-600">{inr(totalPaid)}</div>
                        <div className="text-xs text-slate-500 mt-1">{totalInvoiced > 0 ? Math.round((totalPaid / totalInvoiced) * 100) : 0}% collected</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6 text-center">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Outstanding</div>
                        <div className={`text-2xl font-black ${outstanding > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{inr(outstanding)}</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6 text-center">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">PO Spend (Costs)</div>
                        <div className="text-2xl font-black text-red-500">{inr(totalPOSpend)}</div>
                        <div className="text-xs text-slate-500 mt-1">{pos?.length ?? 0} purchase orders</div>
                    </CardContent>
                </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border-t-4 border-t-emerald-500">
                    <CardContent className="pt-6">
                        <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4">Profit Margin</div>
                        <div className="flex items-end gap-3">
                            <div className={`text-4xl font-black ${margin >= 20 ? 'text-emerald-600' : margin >= 0 ? 'text-amber-600' : 'text-red-600'}`}>
                                {margin.toFixed(1)}%
                            </div>
                            <div className="text-sm text-slate-500 pb-1">
                                {inr(totalInvoiced - totalPOSpend)} net profit
                            </div>
                        </div>
                        <div className="mt-4 bg-slate-100 rounded-full h-3 overflow-hidden">
                            <div
                                className={`h-full rounded-full transition-all ${margin >= 20 ? 'bg-emerald-500' : margin >= 0 ? 'bg-amber-500' : 'bg-red-500'}`}
                                style={{ width: `${Math.max(0, Math.min(100, margin))}%` }}
                            />
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-t-4 border-t-blue-500">
                    <CardContent className="pt-6">
                        <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4">Budget vs Actual</div>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-slate-600">Estimated Value</span>
                                <span className="font-bold">{inr(estimated)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-slate-600">Actual Revenue (Invoiced)</span>
                                <span className="font-bold">{inr(totalInvoiced)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-slate-600">Applied Costs (Entries)</span>
                                <span className="font-bold text-indigo-600">{inr(totalCostEntries)}</span>
                            </div>
                            <div className="border-t pt-3 flex justify-between items-center">
                                <span className="text-sm font-bold text-slate-700">Revenue Variance</span>
                                {variance === null ? (
                                    <span className="font-medium text-sm text-slate-400 italic">
                                        {estimated > 0 ? 'No revenue yet' : 'No budget set'}
                                    </span>
                                ) : (
                                    <span className={`font-black text-lg ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {variance >= 0 ? '+' : ''}{variance.toFixed(1)}%
                                    </span>
                                )}
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-bold text-slate-700">Cost vs Budget</span>
                                {costVariance === null ? (
                                    <span className="font-medium text-sm text-slate-400 italic">
                                        {estimated > 0 ? 'No costs yet' : 'No budget set'}
                                    </span>
                                ) : (
                                    <span className={`font-black text-lg ${costVariance <= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {costVariance >= 0 ? '+' : ''}{costVariance.toFixed(1)}%
                                    </span>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <ProjectCostEntries onTotalChange={setTotalCostEntries} />
        </div>
    );
};

const ProjectShowContent = () => {
    const record = useRecordContext();
    if (!record) return null;

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-6">
            {/* Header Section */}
            <div className="flex justify-between items-start">
                <div className="space-y-1">
                    <div className="flex items-center gap-4">
                        <h2 className="text-3xl font-bold tracking-tight text-slate-900">{record.name}</h2>
                        <StatusBadge status={record.status} />
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span className="font-mono bg-slate-100 px-2 py-0.5 rounded border text-[10px]">REF: {record.reference}</span>
                        <span className="text-slate-300">|</span>
                        <ReferenceField source="contact_id" reference="contacts" link="show">
                            <span className="text-primary hover:underline font-medium">
                                <TextField source="first_name" /> <TextField source="last_name" />
                            </span>
                        </ReferenceField>
                    </div>
                </div>
                <div className="text-right">
                    <ReferenceField source="project_manager" reference="sales" link="show">
                        <div className="flex items-center gap-2 text-xs bg-slate-50 px-3 py-1.5 rounded-full border border-slate-200">
                            <User className="w-3.5 h-3.5 text-slate-400" />
                            <span className="font-medium text-slate-600">PM: <TextField source="first_name" /> <TextField source="last_name" /></span>
                        </div>
                    </ReferenceField>
                </div>
            </div>

            <Tabs defaultValue="overview" className="w-full">
                <TabsList className="grid w-full grid-cols-5 md:w-[620px]">
                    <TabsTrigger value="overview" className="flex items-center gap-2">
                        <Briefcase className="w-3.5 h-3.5" /> Overview
                    </TabsTrigger>
                    <TabsTrigger value="design" className="flex items-center gap-2">
                        <Wrench className="w-3.5 h-3.5" /> Design
                    </TabsTrigger>
                    <TabsTrigger value="documents" className="flex items-center gap-2">
                        <FileStack className="w-3.5 h-3.5" /> Docs
                    </TabsTrigger>
                    <TabsTrigger value="vendors" className="flex items-center gap-2">
                        <Users className="w-3.5 h-3.5" /> Vendors
                    </TabsTrigger>
                    <TabsTrigger value="financials" className="flex items-center gap-2">
                        <CircleDollarSign className="w-3.5 h-3.5" /> Financials
                    </TabsTrigger>
                </TabsList>
                
                <TabsContent value="overview">
                    <ProjectOverview />
                </TabsContent>
                
                <TabsContent value="design">
                    <ProjectDesign />
                </TabsContent>
                
                <TabsContent value="documents">
                    <ProjectDocuments />
                </TabsContent>
                
                <TabsContent value="vendors">
                    <ProjectVendors />
                </TabsContent>

                <TabsContent value="financials">
                    <ProjectFinancials />
                </TabsContent>
            </Tabs>
        </div>
    );
};

export const ProjectShow = () => {
    return (
        <Show actions={<ProjectShowActions />}>
            <ProjectShowContent />
        </Show>
    );
};
